import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { PersonPlus, Person } from "react-bootstrap-icons";
import { ChipsRow, MetaChip, ChipLabel, ChipValue } from "../ui/Chips";

/**
 * Who this sheet is for, and the measurements read off it.
 *
 * The same shape as a bowler's own header, because that is what this is
 * about to become. It replaced a grid of dropdowns: a measurement in this
 * application is a chip you tap, and showing the scan's version of the same
 * numbers as a form was most of why the review read as a different program.
 *
 * The bowler's numbers are only ever written on a bowler being created here.
 * An existing record's PAP was measured by somebody, and a scan is no reason
 * to overwrite it -- so against an existing bowler the chips go quiet.
 */

const Head = styled.div`
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`;

const Avatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #e7eaf6;
  color: #00187e;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  flex: 0 0 auto;
`;

const Who = styled.div`
  flex: 0 0 auto;
  min-width: 172px;
  max-width: 260px;
`;

const Name = styled.button`
  display: block;
  max-width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  text-align: left;
  border-radius: 4px;
  font-size: 15px;
  font-weight: 700;
  color: #202124;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Contact = styled.div`
  font-size: 11px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Destination = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  /* Touch-first: tapped on a tablet at a shop counter. */
  min-height: 44px;
  padding: 6px 16px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$new ? "#00187e" : "#c3c7d4")};
  background: ${(p) => (p.$new ? "#e7eaf6" : "#ffffff")};
  color: #00187e;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  flex: 0 0 auto;

  &:disabled {
    cursor: default;
  }

  &:not(:disabled):hover {
    background: #d8defa;
  }
`;

const Warn = styled(MetaChip)`
  background: #fef5e1;
  border-color: #f5a524;

  &:hover {
    background: ${(p) => (p.$readonly ? "#fef5e1" : "#fdecc8")};
  }
`;

const initialsOf = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "?";

const ScanReviewHeader = ({
  name,
  contact = {},
  hand,
  bowler = {},
  targetName,
  isExisting,
  onChangeDestination,
  onPick,
  onPickName,
  readOnly = false,
}) => {
  const { t } = useTranslation();

  const papText = [bowler.pap_h, bowler.pap_v].filter(Boolean).join(" × ");
  const pap = papText
    ? `${papText}${bowler.pap_ud && bowler.pap_ud !== "-" ? ` ${bowler.pap_ud}` : ""}`
    : "";

  const contactLine = [contact.phone, contact.email].filter(Boolean).join(" · ");

  // A chip with nothing in it says so and stays tappable: an empty chip that
  // reads "Set" is an invitation, where a blank one just looks broken.
  const chip = (key, label, value, field, alert) => {
    const missing = !value;
    const Chip = missing ? Warn : MetaChip;
    return (
      <Chip
        key={key}
        type="button"
        $readonly={readOnly}
        $alert={alert && !missing}
        disabled={readOnly}
        title={`${label} ${value || ""}`.trim()}
        onClick={() => !readOnly && onPick(field)}
      >
        <ChipLabel>{label}</ChipLabel>
        <ChipValue $alert={alert && !missing}>
          {value || t("specsheetScan.bowler.setValue")}
        </ChipValue>
      </Chip>
    );
  };

  // Matched loosely because this value comes off a scanned sheet: the reader
  // may hand back "left_hander", "Left handed" or just "Left".
  const isLeftHanded = String(hand || "").toLowerCase().includes("left");

  return (
    <Head>
      <Avatar>{initialsOf(targetName || name)}</Avatar>
      <Who>
        {/* A misread name is the one value on this screen that decides who
            the record belongs to, so it opens its picker like everything
            else. Against an existing bowler it is their name, not the
            sheet's, and there is nothing here to correct. */}
        <Name
          type="button"
          disabled={!!targetName}
          onClick={() => !targetName && onPickName && onPickName()}
        >
          {targetName || name || t("specsheetScan.batch.noName")}
        </Name>
        {contactLine ? <Contact>{contactLine}</Contact> : null}
      </Who>
      <ChipsRow>
        {chip("hand", t("specsheetScan.bowler.hand"), hand, "hand", isLeftHanded)}
        {chip("pap", t("form.label.pap"), pap, "pap")}
        {chip("style", t("specsheetScan.bowler.style"), bowler.style, "style")}
        {chip(
          "axis_rot",
          t("form.label.axis_rotation"),
          bowler.axis_rot,
          "axis_rot",
        )}
        {chip("axis_tilt", t("form.label.tilt"), bowler.axis_tilt, "axis_tilt")}
        {chip(
          "ball_speed",
          t("form.label.speed"),
          bowler.ball_speed,
          "ball_speed",
        )}
        {chip(
          "rev_rate",
          t("form.label.rev_rate"),
          bowler.rev_rate,
          "rev_rate",
        )}
      </ChipsRow>
      <Destination
        type="button"
        $new={!isExisting}
        disabled={!onChangeDestination}
        onClick={onChangeDestination}
      >
        {isExisting ? <Person size={14} /> : <PersonPlus size={14} />}
        {/* Both halves carry a name. Asking for "New bowler: {{name}}" and
            passing nothing left the placeholder on screen, which is what the
            button has been reading since the review became a screen. A sheet
            with no name read has nothing to put there, so it says the plain
            thing instead of "New bowler: ". */}
        {isExisting
          ? t("specsheetScan.batch.goesTo", { name: targetName })
          : name
            ? t("specsheetScan.batch.newBowler", { name })
            : t("specsheetScan.batch.newBowlerUnnamed")}
      </Destination>
    </Head>
  );
};

export default ScanReviewHeader;
